import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, ArrowDownCircle, ArrowUpCircle, Trash2, Edit2, Save, X, AlertTriangle, Package, FolderKanban, ChevronDown, ChevronUp, Search, FileText } from 'lucide-react';
import { MediaViewer } from '../components/shared/MediaComponents';
import ChatInput from '../components/shared/ChatInput';
import { formatDate, today } from '../utils/dateFormat';

function deadlineDays(d) {
  if (!d) return null;
  const t = new Date(); t.setHours(0,0,0,0);
  const dl = new Date(d); dl.setHours(0,0,0,0);
  return Math.ceil((dl - t) / 86400000);
}
function DeadlineBadge({ deadline, className = '' }) {
  const days = deadlineDays(deadline);
  if (days === null) return null;
  const color = days < 0 ? 'bg-red-500 text-white' : days === 0 ? 'bg-red-100 text-red-700' : days <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600';
  const label = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d left`;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${color} ${className}`}>{days < 0 ? '⚠️' : '📅'} {label}</span>;
}

const TABS = [
  { key: 'orders', label: 'Work Orders' }, /* EXP14 factory board */
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'inout', label: 'In / Out' },
  { key: 'projects', label: 'Projects' },
  { key: 'updates', label: 'Daily Updates' },
];

export default function Factory() {
  const [tab, setTab] = useState('orders');
  const [initProject, setInitProject] = useState(null);

  const goToProject = (projectId) => { setInitProject(projectId); setTab('projects'); };

  return (
    <div className="space-y-4">
      <div><h1 className="text-2xl font-bold text-gray-900">Factory</h1><p className="text-sm text-gray-500">Material inventory, work orders & tracking</p></div>
      <div className="flex gap-1 overflow-x-auto border-b pb-px scrollbar-hide">
        {TABS.map(t => (<button key={t.key} onClick={() => { setTab(t.key); if (t.key !== 'projects') setInitProject(null); }} className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${tab === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>{t.label}</button>))}
      </div>
      {tab === 'orders' && <FactoryBoard />}
      {tab === 'dashboard' && <DashboardTab onGoToProject={goToProject} />}
      {tab === 'inventory' && <InventoryTab />}
      {tab === 'inout' && <InOutTab />}
      {tab === 'projects' && <ProjectsTab initProject={initProject} />}
      {tab === 'updates' && <UpdatesTab />}
      {tab === 'expenses' && <ExpensesTab />}
      {tab === 'files' && <FilesTab />}
      {tab === 'issues' && <IssuesTab />}
    </div>
  );
}

/* ═══ EXP14: FACTORY BOARD — Ongoing / Completed ═══ */
const FACTORY_ROLES = ['admin', 'md']; // add 'factory' when the role exists
const TRANSPORT_ROLES = ['admin', 'md']; // add 'transport' when the role exists
const boardItems = (o) => (Array.isArray(o.items) && o.items.length ? o.items.map((i) => ({ name: i.name || i.material || i.title || '', qty: i.qty ?? i.quantity ?? '', size: i.size || i.measurements || '' })) : [{ name: o.title, qty: o.quantity, size: o.measurements }]);
/* EXP18 */ const ItemPhotos = ({ o, i }) => { const m = (o.item_media || {})[String(i)] || []; return m.length ? <div className="mt-1 mb-1"><MediaViewer attachments={m} /></div> : null; };
function FactoryBoard() {
  const { user } = useAuth();
  const role = user?.role;
  const canFactory = FACTORY_ROLES.includes(role);
  const canTransport = TRANSPORT_ROLES.includes(role);
  const [view, setView] = useState('ongoing');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [upId, setUpId] = useState(null); /* EXP14c */
  const upload = async (o, fl) => {
    if (!fl.length || upId) return; setUpId(o.id); let ok = 0;
    for (const f of fl) { const fd = new FormData(); fd.append('file', f); fd.append('entity_type', 'factory_work_order'); fd.append('entity_id', o.id); try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); ok++; } catch (e) {} }
    setUpId(null); if (ok) toast.success(ok + ' file' + (ok > 1 ? 's' : '') + ' added'); if (ok < fl.length) toast.error((fl.length - ok) + ' upload(s) failed'); load();
  };
  const load = () => {
    setErr('');
    api.get('/factory/work-orders/board', { params: { view } }).then((r) => { setOrders(r.data.orders || []); setCounts(r.data.counts || {}); setLoading(false); })
      .catch((e) => { setLoading(false); setErr(e?.response?.data?.error || 'Could not load work orders'); });
  };
  useEffect(() => { setLoading(true); load(); }, [view]);
  const act = async (o, path, body, msg) => {
    if (busy) return; setBusy(o.id);
    try { await api.put(`/factory/work-orders/${o.id}/${path}`, body); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
    setBusy(null);
  };
  const complete = (o) => { if (window.confirm(`Mark ${o.wo_number || 'this order'} as COMPLETED?`)) act(o, 'mark-completed', {}, 'Marked completed ✓'); };
  const dispatch = (o) => { const note = window.prompt('Dispatched — vehicle / driver / note (optional):', ''); if (note === null) return; act(o, 'mark-dispatched', { note }, 'Marked dispatched 🚚'); };
  const undo = (o) => { if (window.confirm(o.dispatched_at ? 'Undo dispatch?' : 'Move back to Ongoing?')) act(o, 'undo', {}, 'Undone'); };
  const fmtD = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); } catch (e) { return ''; } };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setView('ongoing')} className={`py-3 rounded-xl text-sm font-bold ${view === 'ongoing' ? 'bg-brand-600 text-white' : 'bg-white border text-gray-600'}`}>🔧 Ongoing ({counts.ongoing ?? '…'})</button>
        <button onClick={() => setView('completed')} className={`py-3 rounded-xl text-sm font-bold ${view === 'completed' ? 'bg-green-600 text-white' : 'bg-white border text-gray-600'}`}>✅ Completed ({counts.completed ?? '…'}){counts.to_dispatch > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] align-middle">{counts.to_dispatch}</span>}</button>
      </div>
      {view === 'completed' && counts.to_dispatch > 0 && <p className="text-xs text-red-600 font-semibold">{counts.to_dispatch} completed — waiting to be dispatched</p>}
      {err && <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between"><p className="text-sm text-red-600">{err}</p><button onClick={load} className="px-3 py-1.5 bg-brand-600 text-white rounded-lg text-xs">Retry</button></div>}
      {loading ? <p className="text-center text-gray-400 text-sm py-10">Loading…</p> : orders.length === 0 ? <p className="text-center text-gray-400 text-sm py-10 bg-white rounded-xl border">{view === 'ongoing' ? 'No ongoing work orders 🎉' : 'No completed work orders yet'}</p> : orders.map((o) => {
        const dd = o.deadline ? deadlineDays(o.deadline) : null;
        const open = openId === o.id;
        return (
          <div key={o.id} className={`bg-white rounded-xl border p-4 ${o.priority === 'urgent' && view === 'ongoing' ? 'border-red-300' : ''} ${view === 'ongoing' && dd !== null && dd < 0 ? 'bg-red-50' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {o.wo_number && <span className="text-[11px] font-mono text-gray-400">{o.wo_number}</span>}
                  {o.priority === 'urgent' && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-600 text-white">🔥 URGENT</span>}
                  {view === 'ongoing' && o.deadline && <DeadlineBadge deadline={o.deadline} />}
                  {view === 'completed' && (o.dispatched_at ? <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-700">🚚 Dispatched {fmtD(o.dispatched_at)}</span> : <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700">Ready to dispatch</span>)}
                </div>
                <p className="text-sm font-bold text-gray-900 mt-1 flex items-center gap-1"><FolderKanban size={13} className="text-brand-600" /> {o.project?.name || o.custom_project_name || 'No project'}</p>
                {o.project?.location && <p className="text-xs text-gray-500">📍 {o.project.location}</p>}
              </div>
              <button onClick={() => setOpenId(open ? null : o.id)} className="p-1 text-gray-400 shrink-0">{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</button>
            </div>
            <div className="mt-2 bg-gray-50 rounded-lg px-3 py-2 space-y-1">
              {boardItems(o).map((it, i) => <div key={i}><p className="text-sm"><span className="font-semibold">{it.name}</span><span className="text-gray-500">{it.qty ? ` · Qty ${it.qty}` : ''}{it.size ? ` · ${it.size}` : ''}</span></p><ItemPhotos o={o} i={i} /></div>)}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">by {o.creator?.name || '—'}{o.completed_at ? ` · completed ${fmtD(o.completed_at)}` : ''}{o.dispatch_note ? ` · ${o.dispatch_note}` : ''}</p>
            {open && (
              <div className="mt-2 space-y-2">
                {o.description && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 whitespace-pre-line">{o.description}</p>}
              </div>
            )}
            {(o.media || []).length > 0 && <div className="mt-2"><MediaViewer attachments={o.media} /></div>}
            <label className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 border border-dashed border-brand-300 rounded-lg text-xs font-semibold text-brand-700 bg-brand-50 cursor-pointer">
              📷 {upId === o.id ? 'Uploading…' : 'Add photo / file'}
              <input type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx" className="hidden" onChange={(e) => { const fl = Array.from(e.target.files || []); e.target.value = ''; upload(o, fl); }} />
            </label>
            <div className="flex gap-2 mt-3">
              {view === 'ongoing' && canFactory && <button onClick={() => complete(o)} disabled={busy === o.id} className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">✅ Mark Completed</button>}
              {view === 'completed' && !o.dispatched_at && canTransport && <button onClick={() => dispatch(o)} disabled={busy === o.id} className="flex-1 py-3 bg-purple-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">🚚 Mark Dispatched</button>}
              {view === 'completed' && ['admin', 'md'].includes(role) && <button onClick={() => undo(o)} disabled={busy === o.id} className="px-4 py-3 border rounded-xl text-xs text-gray-600 disabled:opacity-50">Undo</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══ DASHBOARD ═══ */
function DashboardTab({ onGoToProject }) {
  const [data, setData] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  useEffect(() => { api.get('/factory/dashboard').then(r => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (showCompleted) api.get('/factory/work-orders?status=completed').then(r => setCompletedOrders(r.data.orders || [])).catch(() => {});
  }, [showCompleted]);
  if (!data) return null;

  // Group completed by project
  const completedByProject = {};
  completedOrders.forEach(o => {
    const pName = o.project?.name || 'No Project';
    const pId = o.project?.id || 'none';
    if (!completedByProject[pId]) completedByProject[pId] = { name: pName, location: o.project?.location || '', orders: [] };
    completedByProject[pId].orders.push(o);
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Total Orders</p><p className="text-2xl font-bold text-brand-600">{data.totalOrders}</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Pending</p><p className="text-2xl font-bold text-amber-600">{data.pending}</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">In Progress</p><p className="text-2xl font-bold text-blue-600">{data.inProgress}</p></div>
        <div className="bg-white rounded-xl border p-4 text-center cursor-pointer hover:ring-2 hover:ring-green-300 transition" onClick={() => setShowCompleted(!showCompleted)}>
          <p className="text-xs text-gray-500">Completed</p><p className="text-2xl font-bold text-green-600">{data.completed}</p>
          <p className="text-[10px] text-green-600 font-bold mt-1">{showCompleted ? '▲ Hide' : '▼ View all'}</p>
        </div>
      </div>

      {/* Active projects */}
      {!showCompleted && (
        <>
          <h3 className="font-semibold text-sm text-gray-700">Project-wise Factory Progress</h3>
          {data.projectSummary?.map(p => {
            const pendingCount = (p.orders || []).filter(o => o.status === 'pending').length;
            const inProgressCount = (p.orders || []).filter(o => o.status === 'in_progress').length;
            return (
            <div key={p.id} className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition" onClick={() => onGoToProject(p.id)}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{p.name}</p>
                  {pendingCount > 0 && <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">{pendingCount} pending</span>}
                  {inProgressCount > 0 && <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full font-bold">{inProgressCount} active</span>}
                </div>
                <span className="text-lg font-bold text-brand-600">{p.avgProgress}%</span>
              </div>
              <p className="text-xs text-gray-400 mb-2">{p.location} · {p.orderCount} work orders</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3"><div className="bg-brand-600 h-2.5 rounded-full transition-all" style={{ width: `${p.avgProgress}%` }} /></div>
              <div className="space-y-1">
                {p.orders?.map(o => {
                  const col = o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';
                  return (
                    <div key={o.id} className="flex items-center justify-between text-sm hover:bg-gray-50 rounded-lg px-2 py-1.5 -mx-2">
                      <span className="flex-1 truncate">{o.title}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {o.deadline && o.status !== 'completed' && <DeadlineBadge deadline={o.deadline} />}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${col}`}>{o.status?.replace('_', ' ')}</span>
                        <span className="text-xs font-medium w-8 text-right">{o.progress_percentage}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-brand-600 mt-2 font-medium">Tap to open →</p>
            </div>
            );
          })}
          {(!data.projectSummary || data.projectSummary.length === 0) && <p className="text-center text-gray-400 text-sm py-6">No factory work orders yet. Go to Projects tab to create one.</p>}
        </>
      )}

      {/* Completed WOs section */}
      {showCompleted && (
        <CompletedSection orders={completedOrders} byProject={completedByProject} onClose={() => setShowCompleted(false)} />
      )}
    </div>
  );
}

function CompletedSection({ orders, byProject, onClose }) {
  const [expandedId, setExpandedId] = useState(null);
  const fmtD = d => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return d; } };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-gray-700">Completed Factory Work Orders ({orders.length})</h3>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 font-medium">← Back to Active</button>
      </div>
      {Object.entries(byProject).map(([pId, proj]) => (
        <div key={pId} className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <div><p className="font-semibold">{proj.name}</p><p className="text-xs text-gray-400">{proj.location} · {proj.orders.length} completed</p></div>
            <span className="text-[10px] bg-green-500 text-white px-2.5 py-1 rounded-full font-bold">{proj.orders.length} done</span>
          </div>
          <div className="space-y-1.5">
            {proj.orders.map(o => {
              const exp = expandedId === o.id;
              return (
                <div key={o.id} className={`rounded-lg border overflow-hidden ${exp ? 'border-green-300' : 'border-transparent'}`}>
                  <div className="flex items-center justify-between text-sm bg-green-50 px-3 py-2.5 cursor-pointer hover:bg-green-100 transition" onClick={() => setExpandedId(exp ? null : o.id)}>
                    <div className="flex-1 min-w-0">
                      <span className="truncate block font-medium text-gray-700">{o.title}</span>
                      {o.wo_number && <span className="text-[10px] text-gray-400 font-mono">{o.wo_number}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">completed</span>
                      <span className="text-xs font-bold text-green-700">{o.progress_percentage || 100}%</span>
                      <ChevronDown size={14} className={`text-gray-400 transition ${exp ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {exp && (
                    <div className="px-3 py-3 bg-white border-t space-y-2">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div><p className="text-[10px] uppercase text-gray-400 font-bold">Project</p><p className="text-gray-700 font-medium">{o.project?.name || '-'}</p></div>
                        <div><p className="text-[10px] uppercase text-gray-400 font-bold">Location</p><p className="text-gray-700">{o.project?.location || '-'}</p></div>
                        <div><p className="text-[10px] uppercase text-gray-400 font-bold">Created By</p><p className="text-gray-700">{o.creator?.name || '-'}</p></div>
                        {o.quantity > 1 && <div><p className="text-[10px] uppercase text-gray-400 font-bold">Quantity</p><p className="text-gray-700">{o.quantity} {o.unit || ''}</p></div>}
                        {o.measurements && <div><p className="text-[10px] uppercase text-gray-400 font-bold">Measurements</p><p className="text-gray-700">{o.measurements}</p></div>}
                        {o.deadline && <div><p className="text-[10px] uppercase text-gray-400 font-bold">Deadline</p><p className="text-gray-700">{fmtD(o.deadline)}</p></div>}
                        <div><p className="text-[10px] uppercase text-gray-400 font-bold">Progress</p><p className="text-gray-700 font-bold">{o.progress_percentage || 0}%</p></div>
                        <div><p className="text-[10px] uppercase text-gray-400 font-bold">Created</p><p className="text-gray-700">{fmtD(o.created_at || o.createdAt)}</p></div>
                      </div>
                      {o.description && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">{o.description}</p>}
                      {o.media && o.media.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Reference Files</p>
                          <MediaViewer attachments={o.media} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {orders.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No completed work orders yet</p>}
    </div>
  );
}

/* ═══ INVENTORY ═══ */
function InventoryTab() {
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', category: '', quantity: '', unit: '', rate: '', min_stock: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fetchM = () => api.get('/factory/materials').then(r => { setMaterials(r.data.materials); setStats({ total: r.data.totalItems, value: r.data.totalValue, low: r.data.lowStock }); }).catch(() => {});
  useEffect(() => { fetchM(); }, []);
  const add = async (e) => { e.preventDefault(); try { await api.post('/factory/materials', form); setForm({ name: '', category: '', quantity: '', unit: '', rate: '', min_stock: '' }); setShowAdd(false); fetchM(); toast.success('Added'); } catch { toast.error('Failed'); } };
  const startEdit = (m) => { setEditId(m.id); setEditForm({ name: m.name, category: m.category || '', quantity: m.quantity, unit: m.unit || '', rate: m.rate, min_stock: m.min_stock }); };
  const saveEdit = async () => { try { await api.put(`/factory/materials/${editId}`, editForm); setEditId(null); fetchM(); } catch {} };
  const del = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/factory/materials/${id}`); fetchM(); } catch {} };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Items</p><p className="text-2xl font-bold text-brand-600">{stats.total || 0}</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Value</p><p className="text-2xl font-bold text-green-600">₹{Number(stats.value || 0).toLocaleString('en-IN')}</p></div>
        <div className="bg-white rounded-xl border p-4 text-center"><p className="text-xs text-gray-500">Low Stock</p><p className="text-2xl font-bold text-red-600">{stats.low || 0}</p></div>
      </div>
      <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm"><Plus size={16} /> Add Material</button>
      {showAdd && (
        <form onSubmit={add} className="bg-white rounded-xl border p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name *" required className="px-3 py-2.5 border rounded-lg text-sm" />
            <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Category" className="px-3 py-2.5 border rounded-lg text-sm" />
            <div className="flex gap-2"><input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qty" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" /><input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit" className="w-20 px-3 py-2.5 border rounded-lg text-sm" /></div>
          </div>
          <div className="flex gap-3"><input type="number" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} placeholder="₹ Rate per unit" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" /><button type="submit" className="px-6 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium">Add</button></div>
        </form>
      )}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>{['Material', 'Category', 'Qty', 'Unit', 'Rate', 'Value', 'Status', 'Image', ''].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{materials.map(m => {
            const ed = editId === m.id; const val = parseFloat(m.quantity || 0) * parseFloat(m.rate || 0);
            return (<tr key={m.id} className="border-t hover:bg-gray-50">
              <td className="px-4 py-2.5">{ed ? <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="px-2 py-1 border rounded text-sm w-full" /> : <span className="font-medium">{m.name}</span>}</td>
              <td className="px-4 py-2.5">{ed ? <input value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} className="px-2 py-1 border rounded text-sm w-24" /> : (m.category || '-')}</td>
              <td className="px-4 py-2.5">{ed ? <input type="number" value={editForm.quantity} onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))} className="px-2 py-1 border rounded text-sm w-20" /> : <span className={`font-bold ${m.low_stock ? 'text-red-600' : ''}`}>{m.quantity}</span>}</td>
              <td className="px-4 py-2.5">{ed ? <input value={editForm.unit} onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))} className="px-2 py-1 border rounded text-sm w-16" /> : (m.unit || '-')}</td>
              <td className="px-4 py-2.5">{ed ? <input type="number" value={editForm.rate} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} className="px-2 py-1 border rounded text-sm w-20" /> : `₹${Number(m.rate || 0).toLocaleString()}`}</td>
              <td className="px-4 py-2.5 font-medium">₹{Number(val).toLocaleString()}</td>
              <td className="px-4 py-2.5">{m.low_stock ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full"><AlertTriangle size={10} className="inline" /> Low</span> : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">OK</span>}</td>
              <td className="px-4 py-2.5"><MediaViewer attachments={m.media || []} /></td>
              <td className="px-4 py-2.5"><div className="flex gap-1">{ed ? (<><button onClick={saveEdit} className="p-1 text-green-600"><Save size={14} /></button><button onClick={() => setEditId(null)} className="p-1 text-gray-400"><X size={14} /></button></>) : (<><button onClick={() => startEdit(m)} className="p-1 text-gray-400 hover:text-brand-600"><Edit2 size={14} /></button><button onClick={() => del(m.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button></>)}</div></td>
            </tr>);
          })}</tbody>
        </table>
        {materials.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No materials yet</p>}
      </div>
      {materials.length > 0 && <div className="bg-white rounded-xl border p-3"><p className="text-xs text-gray-500 mb-2">Attach images to materials</p>{materials.map(m => (<div key={m.id} className="flex items-center gap-3 py-2 border-b last:border-0"><span className="text-sm font-medium w-40 truncate">{m.name}</span><div className="flex-1"><ChatInput entityType="factory_material" entityId={m.id} onFileUploaded={fetchM} onSend={async () => {}} placeholder="Photo..." /></div></div>))}</div>}
    </div>
  );
}

/* ═══ IN / OUT ═══ */
function InOutTab() {
  const [materials, setMaterials] = useState([]);
  const [txns, setTxns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [useCustom, setUseCustom] = useState(false);
  const [form, setForm] = useState({ material_id: '', custom_material_name: '', custom_unit: '', type: 'in', quantity: '', project_id: '', notes: '' });
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const fetchTxns = () => api.get('/factory/transactions?limit=50').then(r => setTxns(r.data.transactions)).catch(() => {});
  useEffect(() => { api.get('/factory/materials').then(r => setMaterials(r.data.materials)).catch(() => {}); fetchTxns(); api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quantity) { toast.error('Enter quantity'); return; }
    if (!useCustom && !form.material_id) { toast.error('Select a material'); return; }
    if (useCustom && !form.custom_material_name.trim()) { toast.error('Enter material name'); return; }

    try {
      const payload = {
        type: form.type,
        quantity: form.quantity,
        notes: form.notes,
        project_id: form.project_id || null,
        date: today(),
      };
      if (useCustom) {
        payload.custom_material_name = form.custom_material_name.trim();
        payload.custom_unit = form.custom_unit.trim();
      } else {
        payload.material_id = form.material_id;
      }

      const res = await api.post('/factory/transactions', payload);
      const txnId = res.data.transaction?.id;

      // Upload attached images
      if (txnId && files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity_type', 'factory_transaction');
          fd.append('entity_id', txnId);
          try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
        }
      }

      toast.success(form.type === 'in' ? 'Material received ✓' : 'Material sent out ✓');
      setForm(f => ({ ...f, quantity: '', notes: '', custom_material_name: '', custom_unit: '' }));
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      fetchTxns();
      api.get('/factory/materials').then(r => setMaterials(r.data.materials));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Big IN / OUT buttons */}
      <div className="grid grid-cols-2 gap-4">
        <button onClick={() => setForm(f => ({ ...f, type: 'in' }))} className={`p-6 rounded-2xl border-2 text-center transition ${form.type === 'in' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300'}`}>
          <ArrowDownCircle size={40} className={`mx-auto mb-2 ${form.type === 'in' ? 'text-green-600' : 'text-gray-400'}`} />
          <p className={`text-lg font-bold ${form.type === 'in' ? 'text-green-700' : 'text-gray-600'}`}>📦 MATERIAL IN</p>
          <p className="text-xs text-gray-400 mt-1">Received at factory</p>
        </button>
        <button onClick={() => setForm(f => ({ ...f, type: 'out' }))} className={`p-6 rounded-2xl border-2 text-center transition ${form.type === 'out' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-red-300'}`}>
          <ArrowUpCircle size={40} className={`mx-auto mb-2 ${form.type === 'out' ? 'text-red-600' : 'text-gray-400'}`} />
          <p className={`text-lg font-bold ${form.type === 'out' ? 'text-red-700' : 'text-gray-600'}`}>📤 MATERIAL OUT</p>
          <p className="text-xs text-gray-400 mt-1">Sent to site</p>
        </button>
      </div>

      <form onSubmit={submit} className="bg-white rounded-xl border p-4 space-y-3">
        {/* Toggle: inventory vs custom */}
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => { setUseCustom(false); setForm(f => ({ ...f, custom_material_name: '', custom_unit: '' })); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${!useCustom ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>From Inventory</button>
          <button type="button" onClick={() => { setUseCustom(true); setForm(f => ({ ...f, material_id: '' })); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${useCustom ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Custom Material</button>
        </div>

        {!useCustom ? (
          <select value={form.material_id} onChange={e => setForm(f => ({ ...f, material_id: e.target.value }))} required className="w-full px-3 py-3 border rounded-lg text-sm bg-white"><option value="">Select material...</option>{materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.quantity} {m.unit})</option>)}</select>
        ) : (
          <div className="flex gap-3">
            <input value={form.custom_material_name} onChange={e => setForm(f => ({ ...f, custom_material_name: e.target.value }))} placeholder="Material name *" required className="flex-1 px-3 py-3 border rounded-lg text-sm" />
            <input value={form.custom_unit} onChange={e => setForm(f => ({ ...f, custom_unit: e.target.value }))} placeholder="Unit (kg, pcs...)" className="w-28 px-3 py-3 border rounded-lg text-sm" />
          </div>
        )}

        <div className="flex gap-3">
          <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Quantity *" required className="flex-1 px-3 py-3 border rounded-lg text-sm" />
          {form.type === 'out' && <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} className="flex-1 px-3 py-3 border rounded-lg text-sm bg-white"><option value="">Send to site...</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
        </div>
        <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="w-full px-3 py-3 border rounded-lg text-sm" />

        {/* Image upload */}
        <div>
          <p className="text-xs text-gray-500 font-bold uppercase mb-1">📷 Attach photos (optional)</p>
          <input ref={fileRef} type="file" accept="image/*,video/*,.pdf" multiple onChange={e => setFiles(Array.from(e.target.files))} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
          {files.length > 0 && <p className="text-xs text-green-600 mt-1">📎 {files.length} file(s) selected</p>}
        </div>

        <button type="submit" className={`w-full py-4 text-white rounded-xl text-lg font-bold ${form.type === 'in' ? 'bg-green-600 active:bg-green-700' : 'bg-red-500 active:bg-red-600'}`}>{form.type === 'in' ? '📦 + Receive Material' : '📤 - Send Out Material'}</button>
      </form>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-gray-50"><tr>{['Date', 'Material', 'Type', 'Qty', 'Site', 'Photos', 'Notes', 'By'].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>)}</tr></thead>
          <tbody>{txns.map(t => (<tr key={t.id} className="border-t">
            <td className="px-4 py-2">{formatDate(t.date)}</td>
            <td className="px-4 py-2 font-medium">{t.display_name || t.material?.name || t.custom_material_name || '-'}</td>
            <td className="px-4 py-2">{t.type === 'in' ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">📦 IN</span> : <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">📤 OUT</span>}</td>
            <td className="px-4 py-2 font-bold">{t.quantity} {t.display_unit || t.material?.unit || t.custom_unit || ''}</td>
            <td className="px-4 py-2 text-gray-500">{t.project?.name || '-'}</td>
            <td className="px-4 py-2"><MediaViewer attachments={t.media || []} /></td>
            <td className="px-4 py-2 text-gray-400">{t.notes || '-'}</td>
            <td className="px-4 py-2 text-gray-400">{t.recorder?.name}</td>
          </tr>))}</tbody>
        </table>
        {txns.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No transactions</p>}
      </div>
    </div>
  );
}

/* ═══ FACTORY PROJECTS — Work Orders per site ═══ */
function ProjectsTab({ initProject }) {
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selProject, setSelProject] = useState(initProject || null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', quantity: '', unit: '', measurements: '', deadline: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', location: '', start_date: '', end_date: '' });
  const [showCompleted, setShowCompleted] = useState(false);
  const [woCounts, setWoCounts] = useState({});
  const [woEditId, setWoEditId] = useState(null);
  const [woEditForm, setWoEditForm] = useState({});

  const fetchProjects = () => api.get('/projects').then(r => setProjects(r.data.projects || [])).catch(() => {});
  const fetchWoCounts = () => api.get('/factory/dashboard').then(r => {
    const map = {};
    (r.data.projectSummary || []).forEach(p => { map[p.id] = { total: p.orderCount, orders: p.orders || [] }; });
    setWoCounts(map);
  }).catch(() => {});
  useEffect(() => { fetchProjects(); fetchWoCounts(); }, []);
  useEffect(() => { if (initProject) setSelProject(initProject); }, [initProject]);
  const fetchOrders = (pid) => api.get(`/factory/work-orders?project_id=${pid}`).then(r => setOrders(r.data.orders || [])).catch(() => {});
  useEffect(() => { if (selProject) fetchOrders(selProject); }, [selProject]);

  const createProject = async (e) => {
    e.preventDefault();
    if (!newProject.name || !newProject.start_date || !newProject.end_date) { toast.error('Name, start & end date required'); return; }
    try {
      await api.post('/projects', newProject);
      toast.success('Project created');
      setNewProject({ name: '', location: '', start_date: '', end_date: '' });
      setShowNewProject(false);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create project'); }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/factory/work-orders', { ...form, project_id: selProject });
      const orderId = res.data.order?.id;
      // Upload attached files
      if (orderId && form.files && form.files.length > 0) {
        for (const file of form.files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity_type', 'factory_work_order');
          fd.append('entity_id', orderId);
          try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
        }
      }
      setForm({ title: '', description: '', quantity: '', measurements: '' });
      setShowCreate(false); fetchOrders(selProject); fetchWoCounts(); toast.success('Work order created');
    } catch { toast.error('Failed'); }
  };
  const updateProgress = async (id, val) => { try { await api.put(`/factory/work-orders/${id}`, { progress_percentage: val }); fetchOrders(selProject); fetchWoCounts(); } catch {} };
  const updateStatus = async (id, status) => { try { await api.put(`/factory/work-orders/${id}`, { status }); fetchOrders(selProject); fetchWoCounts(); } catch {} };
  const delOrder = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/factory/work-orders/${id}`); fetchOrders(selProject); fetchWoCounts(); } catch {} };
  const startWoEdit = (o) => { setWoEditId(o.id); setWoEditForm({ title: o.title || '', description: o.description || '', quantity: o.quantity || '', unit: o.unit || '', measurements: o.measurements || '', deadline: o.deadline ? o.deadline.split('T')[0] : '' }); };
  const saveWoEdit = async () => { try { await api.put(`/factory/work-orders/${woEditId}`, woEditForm); setWoEditId(null); fetchOrders(selProject); fetchWoCounts(); toast.success('Updated'); } catch { toast.error('Failed'); } };

  const selProjectData = projects.find(p => p.id === selProject);

  return (
    <div className="space-y-4">
      {!selProject ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Select a project to manage factory work orders</p>
            <button onClick={() => setShowNewProject(!showNewProject)} className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm"><Plus size={16} /> New Project</button>
          </div>

          {showNewProject && (
            <form onSubmit={createProject} className="bg-white rounded-xl border p-4 space-y-3">
              <p className="text-sm font-bold text-gray-700">Create New Project</p>
              <input value={newProject.name} onChange={e => setNewProject(f => ({ ...f, name: e.target.value }))} placeholder="Project name *" required className="w-full px-3 py-2.5 border rounded-lg text-sm" />
              <input value={newProject.location} onChange={e => setNewProject(f => ({ ...f, location: e.target.value }))} placeholder="Location (city / area)" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
              <div className="flex gap-3">
                <div className="flex-1"><label className="text-xs text-gray-500">Start date *</label><input type="date" value={newProject.start_date} onChange={e => setNewProject(f => ({ ...f, start_date: e.target.value }))} required className="w-full px-3 py-2.5 border rounded-lg text-sm" /></div>
                <div className="flex-1"><label className="text-xs text-gray-500">End date *</label><input type="date" value={newProject.end_date} onChange={e => setNewProject(f => ({ ...f, end_date: e.target.value }))} required className="w-full px-3 py-2.5 border rounded-lg text-sm" /></div>
              </div>
              <div className="flex gap-3"><button type="button" onClick={() => setShowNewProject(false)} className="flex-1 py-2.5 border rounded-xl text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium">Create Project</button></div>
            </form>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {projects.map(p => {
              const wo = woCounts[p.id] || { total: 0, orders: [] };
              const pendingCount = wo.orders.filter(o => o.status === 'pending').length;
              const inProgressCount = wo.orders.filter(o => o.status === 'in_progress').length;
              const nearestDays = wo.orders.filter(o => o.deadline && o.status !== 'completed').map(o => deadlineDays(o.deadline)).sort((a,b) => a - b);
              const nearest = nearestDays.length > 0 ? nearestDays[0] : null;
              return (
              <div key={p.id} onClick={() => setSelProject(p.id)} className="bg-white rounded-xl border p-4 hover:bg-blue-50 cursor-pointer transition">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-100 text-brand-700 rounded-xl flex items-center justify-center relative">
                    <FolderKanban size={24} />
                    <span className={`absolute -top-2 -right-2 w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${wo.total > 0 ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{wo.total}</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-gray-400">{p.location}</p>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    {inProgressCount > 0 && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{inProgressCount} active</span>}
                    {pendingCount > 0 && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{pendingCount} pending</span>}
                    {nearest !== null && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${nearest < 0 ? 'bg-red-500 text-white' : nearest <= 3 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {nearest < 0 ? `${Math.abs(nearest)}d overdue` : nearest === 0 ? 'Due today' : `${nearest}d left`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          {projects.length === 0 && <p className="text-center text-gray-400 py-8">No projects. Create projects first.</p>}
        </>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <button onClick={() => { setSelProject(null); setOrders([]); setShowCompleted(false); }} className="text-brand-600 hover:underline text-sm">← Back to projects</button>
            <h2 className="font-bold text-lg">{selProjectData?.name}</h2>
            <span className="text-xs text-gray-400">{selProjectData?.location}</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm"><Plus size={16} /> New Work Order</button>
            <button onClick={() => setShowCompleted(!showCompleted)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border ${showCompleted ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {showCompleted ? '✓ Showing Completed' : 'Show Completed'}
            </button>
          </div>

          {showCreate && (
            <form onSubmit={createOrder} className="bg-white rounded-xl border p-4 space-y-3">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Item name * (e.g. Wardrobe, TV Unit)" required className="w-full px-3 py-2.5 border rounded-lg text-sm" />
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description, notes..." rows={2} className="w-full px-3 py-2.5 border rounded-lg text-sm" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Quantity" className="px-3 py-2.5 border rounded-lg text-sm" />
                <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit (pcs, sqft, rft)" className="px-3 py-2.5 border rounded-lg text-sm" />
                <input value={form.measurements} onChange={e => setForm(f => ({ ...f, measurements: e.target.value }))} placeholder="Measurements (L×W×H)" className="px-3 py-2.5 border rounded-lg text-sm" />
                <div><label className="text-[10px] text-gray-500">Deadline</label><input type="date" value={form.deadline || ''} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              </div>
              <p className="text-xs text-gray-500 font-bold uppercase">Attach reference files (optional)</p>
              <input type="file" accept="image/*,video/*,.pdf,.doc,.docx" multiple onChange={e => setForm(f => ({ ...f, files: Array.from(e.target.files) }))} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
              <div className="flex gap-3"><button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2.5 border rounded-xl text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium">Create</button></div>
            </form>
          )}

          {/* Work orders list */}
          {orders.filter(o => showCompleted ? true : o.status !== 'completed').map(o => {
            const col = o.status === 'completed' ? 'border-green-200 bg-green-50' : o.status === 'in_progress' ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50';
            const statusCol = o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';
            return (
              <div key={o.id} className={`bg-white rounded-xl border ${col} p-4 space-y-3`}>
                {woEditId === o.id ? (
                  /* ── Edit mode ── */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between"><p className="text-xs font-bold text-gray-500 uppercase">Edit Work Order</p><button onClick={() => setWoEditId(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button></div>
                    <input value={woEditForm.title} onChange={e => setWoEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Item name *" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
                    <textarea value={woEditForm.description} onChange={e => setWoEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Description, notes..." rows={2} className="w-full px-3 py-2.5 border rounded-lg text-sm" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <input type="number" value={woEditForm.quantity} onChange={e => setWoEditForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qty" className="px-3 py-2.5 border rounded-lg text-sm" />
                      <input value={woEditForm.unit} onChange={e => setWoEditForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit (pcs, sqft)" className="px-3 py-2.5 border rounded-lg text-sm" />
                      <input value={woEditForm.measurements} onChange={e => setWoEditForm(f => ({ ...f, measurements: e.target.value }))} placeholder="L×W×H" className="px-3 py-2.5 border rounded-lg text-sm" />
                      <input type="date" value={woEditForm.deadline} onChange={e => setWoEditForm(f => ({ ...f, deadline: e.target.value }))} className="px-3 py-2.5 border rounded-lg text-sm" />
                    </div>
                    <div className="flex gap-2"><button onClick={() => setWoEditId(null)} className="flex-1 py-2 border rounded-xl text-sm">Cancel</button><button onClick={saveWoEdit} className="flex-1 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1"><Save size={14} /> Save</button></div>
                  </div>
                ) : (
                  /* ── Display mode ── */
                  <>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-lg">{o.title}</p>
                    <p className="text-xs text-gray-400">{o.quantity && `Qty: ${o.quantity}`}{o.unit && ` ${o.unit}`} {o.measurements && `· ${o.measurements}`} · by {o.creator?.name}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs px-3 py-1.5 rounded-full font-bold ${statusCol}`}>{o.status?.replace('_', ' ')}</span>
                    <button onClick={() => startWoEdit(o)} className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg" title="Edit"><Edit2 size={14} /></button>
                    <button onClick={() => delOrder(o.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
                {o.description && <p className="text-sm text-gray-600">{o.description}</p>}

                {/* Deadline */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">📅 Deadline:</span>
                    <input type="date" value={o.deadline ? o.deadline.split('T')[0] : ''} onChange={e => { api.put(`/factory/work-orders/${o.id}`, { deadline: e.target.value || null }).then(() => fetchOrders(selProject)).catch(() => {}); }} className="px-2 py-1 border rounded text-xs bg-white" />
                  </div>
                  {o.deadline && o.status !== 'completed' && <DeadlineBadge deadline={o.deadline} />}
                </div>

                {/* Progress slider */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between"><span className="text-xs text-gray-500">Progress</span><span className="text-sm font-bold text-brand-600">{o.progress_percentage}%</span></div>
                  <input type="range" min="0" max="100" value={o.progress_percentage} onChange={e => updateProgress(o.id, parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-600" />
                </div>

                {/* Status buttons — always show all 3, current is highlighted */}
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => updateStatus(o.id, 'pending')} className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition ${o.status === 'pending' ? 'bg-amber-500 text-white shadow-md ring-2 ring-amber-300' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'}`}>
                    ⏳ Pending
                  </button>
                  <button onClick={() => updateStatus(o.id, 'in_progress')} className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition ${o.status === 'in_progress' ? 'bg-blue-500 text-white shadow-md ring-2 ring-blue-300' : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'}`}>
                    🔧 In Progress
                  </button>
                  <button onClick={() => updateStatus(o.id, 'completed')} className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition ${o.status === 'completed' ? 'bg-green-500 text-white shadow-md ring-2 ring-green-300' : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'}`}>
                    ✅ Completed
                  </button>
                </div>

                {/* Media — Before & After */}
                <MediaViewer attachments={o.media || []} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1">📎 Before / Reference</p>
                    <ChatInput entityType="factory_work_order" entityId={o.id} onFileUploaded={() => fetchOrders(selProject)} onSend={async () => {}} placeholder="Before photos..." />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1">✅ After / Completed</p>
                    <ChatInput entityType="factory_wo_after" entityId={o.id} onFileUploaded={() => fetchOrders(selProject)} onSend={async () => {}} placeholder="After photos..." />
                  </div>
                </div>
                  </>
                )}
              </div>
            );
          })}
          {orders.filter(o => showCompleted ? true : o.status !== 'completed').length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8 bg-white rounded-xl border">
              {orders.length > 0 && !showCompleted ? `All ${orders.length} work orders completed. Tap "Show Completed" to view.` : 'No work orders for this project. Create your first one above.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ═══ FILES TAB — General factory file manager ═══ */
function FilesTab() {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchFiles = () => api.get('/media/entity/factory_files/all').then(r => setFiles(r.data.attachments || [])).catch(() => setFiles([]));
  useEffect(() => { fetchFiles(); }, []);

  const renameFile = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/factory/file-rename/${id}`, { name: editName.trim() });
      setEditId(null); setEditName(''); fetchFiles(); toast.success('Renamed');
    } catch { toast.error('Rename failed'); }
  };

  const deleteFile = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try { await api.delete(`/media/${id}`); fetchFiles(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const filtered = files.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (f.original_filename || '').toLowerCase().includes(q) || (f.file_name || '').toLowerCase().includes(q);
  });

  const isImage = (f) => (f.mime_type || '').startsWith('image/');

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search files by name..." className="w-full pl-10 pr-3 py-3 border rounded-xl text-sm bg-white" />
      </div>

      {/* Upload */}
      <div className="bg-white rounded-xl border p-3">
        <p className="text-xs text-gray-500 mb-2 font-medium">Upload drawings, documents, reference files</p>
        <ChatInput entityType="factory_files" entityId="all" onFileUploaded={fetchFiles} onSend={async () => {}} placeholder="Add files, photos, PDFs..." />
      </div>

      {/* File grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {filtered.map(f => (
          <div key={f.id} className="bg-white rounded-xl border p-2.5 space-y-2 group hover:shadow-md transition">
            {isImage(f) ? (
              <img src={f.s3_url || `/api/media/file/${f.id}`} alt={f.original_filename} className="w-full h-32 object-cover rounded-lg cursor-pointer" onClick={() => window.open(f.s3_url || `/api/media/file/${f.id}`, '_blank')} />
            ) : (
              <div className="w-full h-32 bg-gray-50 rounded-lg flex flex-col items-center justify-center cursor-pointer border" onClick={() => window.open(f.s3_url || `/api/media/file/${f.id}`, '_blank')}>
                <FileText size={32} className="text-blue-500 mb-1" />
                <span className="text-[10px] text-gray-400 uppercase font-medium">{(f.original_filename || '').split('.').pop()}</span>
              </div>
            )}

            {editId === f.id ? (
              <div className="flex gap-1">
                <input value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && renameFile(f.id)} className="flex-1 px-2 py-1.5 border rounded text-xs min-w-0" autoFocus />
                <button onClick={() => renameFile(f.id)} className="p-1 text-green-600"><Save size={14} /></button>
                <button onClick={() => setEditId(null)} className="p-1 text-gray-400"><X size={14} /></button>
              </div>
            ) : (
              <p className="text-xs text-gray-700 font-medium truncate cursor-pointer" title={f.original_filename} onClick={() => { setEditId(f.id); setEditName(f.original_filename || f.file_name || ''); }}>{f.original_filename || f.file_name}</p>
            )}

            <div className="flex items-center gap-1 text-[10px]">
              <span className="text-gray-400 flex-1">{f.file_size ? `${(f.file_size / 1024).toFixed(0)} KB` : ''}</span>
              <button onClick={() => { setEditId(f.id); setEditName(f.original_filename || f.file_name || ''); }} className="px-1.5 py-0.5 text-brand-600 hover:bg-brand-50 rounded">Rename</button>
              <button onClick={() => deleteFile(f.id)} className="px-1.5 py-0.5 text-red-500 hover:bg-red-50 rounded">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && files.length > 0 && <p className="text-center text-gray-400 text-sm py-6">No files matching "{search}"</p>}
      {files.length === 0 && <p className="text-center text-gray-400 text-sm py-8 bg-white rounded-xl border">No files yet. Use the upload bar above to add drawings, documents, photos.</p>}
    </div>
  );
}

/* ═══ DAILY UPDATES ═══ */
function UpdatesTab() {
  const [attachments, setAttachments] = useState([]);
  const [date, setDate] = useState(today());
  const fetchU = () => api.get(`/media/entity/factory_update/${date}`).then(r => setAttachments(r.data.attachments || [])).catch(() => setAttachments([]));
  useEffect(() => { fetchU(); }, [date]);
  return (
    <div className="space-y-4">
      <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
      <ChatInput entityType="factory_update" entityId={date} onFileUploaded={fetchU} onSend={async () => { toast.success('Posted'); }} placeholder="Factory daily update — photos, notes..." />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{attachments.map(a => (<div key={a.id} className="bg-white rounded-xl border p-2"><MediaViewer attachments={[a]} /><div className="flex items-center justify-between mt-1 gap-1"><p className="text-[10px] text-gray-400 truncate">{a.file_name}</p><button onClick={() => delA(a.id)} className="text-[10px] font-semibold text-red-500 hover:bg-red-50 rounded px-1.5 py-0.5 shrink-0">Delete</button></div></div>))}</div>
      {attachments.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No updates for {formatDate(date)}</p>}
    </div>
  );
}

/* ═══ EXPENSES ═══ */
function ExpensesTab() {
  const [txns, setTxns] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const fetchT = () => api.get('/factory/transactions?limit=100').then(r => setTxns(r.data.transactions)).catch(() => {});
  const fetchA = () => api.get('/media/entity/factory_expense/all').then(r => setAttachments(r.data.attachments || [])).catch(() => setAttachments([]));
  const delA = async (id) => { if (!window.confirm('Delete this bill?')) return; try { await api.delete('/media/' + id); fetchA(); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  useEffect(() => { fetchT(); fetchA(); }, []);
  const total = txns.reduce((s, t) => s + (parseFloat(t.quantity) || 0) * (parseFloat(t.material?.rate) || 0), 0);
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-4"><p className="text-sm text-gray-500">Estimated material value</p><p className="text-2xl font-bold text-brand-600">₹{Number(total).toLocaleString('en-IN')}</p></div>
      <div className="bg-white rounded-xl border p-3"><p className="text-xs text-gray-500 mb-2">Upload bills, receipts, invoices</p><ChatInput entityType="factory_expense" entityId="all" onFileUploaded={fetchA} onSend={async () => { toast.success('Saved'); }} placeholder="Add expense receipt, bill..." /></div>
      {attachments.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{attachments.map(a => (<div key={a.id} className="bg-white rounded-xl border p-2"><MediaViewer attachments={[a]} /><p className="text-[10px] text-gray-400 mt-1">{a.file_name}</p></div>))}</div>}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-gray-50"><tr>{['Date', 'Material', 'Type', 'Qty', 'Rate', 'Value', 'Site'].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>)}</tr></thead>
          <tbody>{txns.map(t => { const v = (parseFloat(t.quantity) || 0) * (parseFloat(t.material?.rate) || 0); return (<tr key={t.id} className="border-t"><td className="px-4 py-2">{formatDate(t.date)}</td><td className="px-4 py-2 font-medium">{t.material?.name}</td><td className="px-4 py-2">{t.type === 'in' ? '📦 IN' : '📤 OUT'}</td><td className="px-4 py-2">{t.quantity} {t.material?.unit}</td><td className="px-4 py-2">₹{Number(t.material?.rate || 0).toLocaleString()}</td><td className="px-4 py-2 font-bold">₹{Number(v).toLocaleString()}</td><td className="px-4 py-2 text-gray-400">{t.project?.name || '-'}</td></tr>); })}</tbody>
        </table>
        {txns.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No data</p>}
      </div>
    </div>
  );
}

/* ═══ ISSUES ═══ */
function IssuesTab() {
  const [attachments, setAttachments] = useState([]);
  const fetchI = () => api.get('/media/entity/factory_issue/all').then(r => setAttachments(r.data.attachments || [])).catch(() => setAttachments([]));
  const delI = async (id) => { if (!window.confirm('Delete this issue?')) return; try { await api.delete('/media/' + id); fetchI(); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  useEffect(() => { fetchI(); }, []);
  return (
    <div className="space-y-4">
      <ChatInput entityType="factory_issue" entityId="all" onFileUploaded={fetchI} onSend={async () => { toast.success('Issue reported'); }} placeholder="Report factory issue with photos, voice..." sendColor="red" />
      {attachments.map(a => (<div key={a.id} className="bg-white rounded-xl border p-3"><MediaViewer attachments={[a]} /><div className="flex items-center justify-between gap-1"><p className="text-[10px] text-gray-400 truncate">{a.file_name} · {formatDate(a.created_at)}</p><button onClick={() => delI(a.id)} className="text-[10px] font-semibold text-red-500 hover:bg-red-50 rounded px-1.5 py-0.5 shrink-0">Delete</button></div></div>))}
      {attachments.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No issues</p>}
    </div>
  );
}
